import { useState, useEffect } from "react";
import { Controller, FieldError } from "react-hook-form";
import { Box, Button, Autocomplete, TextField, Chip } from "@mui/material";
import { Settings as SettingsIcon } from "@mui/icons-material";
import { Vocabulary } from "../../Utils/Vocabulary";
import DocumentTypeManagerModal from "./DocumentTypeManagerModal";
import { getData } from "../../Services/getData";
import { endpoints } from "../../Utils/UrlEnum";

/**
 * Reusable DocumentType Autocomplete component with management dialog
 *
 * Usage:
 * <DocumentTypeAutocomplete
 *   control={control}
 *   name="documentTypeId"
 *   showManageButton={true}
 *   error={errors.documentTypeId}
 * />
 */

interface IDocumentTypeAutocompleteProps {
  control: any;
  name?: string;
  showManageButton?: boolean;
  error?: FieldError;
  multiple?: boolean;
}

export default function DocumentTypeAutocomplete({
  control,
  name = "documentTypeId",
  showManageButton = true,
  error,
  multiple = false,
}: IDocumentTypeAutocompleteProps) {
  const [documentTypes, setDocumentTypes] = useState<any[]>([]);
  const [allDocumentTypes, setAllDocumentTypes] = useState<any[]>([]);
  const [openManageDialog, setOpenManageDialog] = useState(false);

  useEffect(() => {
    fetchDocumentTypes();
  }, []);

  const fetchDocumentTypes = async () => {
    try {
      const response = await getData(endpoints.documentTypes);
      if (response?.data) {
        setAllDocumentTypes(response.data);
        // const filteredTypes = response.data.filter(
        //   (type: any) => type.isTemplate === null || type.isTemplate === false
        // );
        setDocumentTypes(response.data);
      }
    } catch (error) {
      console.error("Error fetching document types:", error);
    }
  };

  return (
    <>
      {showManageButton && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setOpenManageDialog(true)}
            startIcon={<SettingsIcon />}
          >
            {Vocabulary.manageTypes}
          </Button>
        </Box>
      )}

      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Autocomplete
            {...field}
            multiple={multiple}
            options={documentTypes}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option?.name || ""
            }
            isOptionEqualToValue={(option, value) => {
              if (typeof option === "string") return option === value;
              if (typeof value === "string") return option?._id === value;
              return option?._id === value?._id;
            }}
            renderOption={(props, option) => (
              <li {...props} key={option._id}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    gap: 1,
                  }}
                >
                  <span>{option?.name}</span>
                  {option?.isTemplate && (
                    <Chip
                      label={Vocabulary.template}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ height: 20, fontSize: "0.7rem", fontWeight: 600 }}
                    />
                  )}
                </Box>
              </li>
            )}
            fullWidth
            size="small"
            value={
              multiple
                ? (field.value || []).map((id: string) =>
                    documentTypes.find((dt) => dt._id === id)
                  ).filter(Boolean)
                : field.value
                ? documentTypes.find((dt) => dt._id === field.value) || null
                : null
            }
            onChange={(_, newValue) => {
              if (multiple) {
                field.onChange((newValue as any[]).map((v) => v._id));
              } else {
                field.onChange((newValue as any)?._id || null);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={Vocabulary.documentType}
                size="small"
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        )}
      />

      <DocumentTypeManagerModal
        open={openManageDialog}
        onClose={() => setOpenManageDialog(false)}
        documentTypes={allDocumentTypes}
        onRefresh={fetchDocumentTypes}
      />
    </>
  );
}
